import { useGeckoPermissions } from 'features/permissions'
import {
  nullablePlanReducer,
  PlanAction,
} from 'features/sentinelDesign/planEdits'
import { SentinelStageId } from 'features/sentinelDesign/stages'
import { SentinelPlan } from 'features/sentinelDesign/types'
import { useSentinelDesignApi } from 'features/sentinelDesign/useSentinelDesignApi'
import {
  buildRemovedPointOverlays,
  ExperimentRemovedPoints,
} from 'features/timeSeries/experiment/overlays'
import { useExperimentApi } from 'features/timeSeries/experiment/useExperimentApi'
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import { BaseSensorEntry, PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

/** Where the builder's tags come from: an asset template, a picked asset
 * instance, or a pasted tag list (the throwaway bypass). */
export type SentinelSource = 'template' | 'instance' | 'paste'

/**
 * Session state of the in-viewer Sentinels tab — the customer-facing agentic
 * model builder + monitor.
 *
 * Mirrors {@link ExperimentLabProvider}: it lives in a provider mounted by
 * `ScatterplotProvider` (NOT the tab component) so switching sidebar tabs or
 * closing the sidebar never discards an in-progress build, a draft plan, or the
 * open detail view. The chart-facing side effects it will own — streaming the
 * agent's chosen tags onto the chart as pens, marking the split, drawing the
 * fitted band — are wired in the milestones step; this container holds the
 * state they read.
 *
 * The staged design flow itself is reused wholesale (`useSentinelDesignApi`,
 * the `nullablePlanReducer`), exactly as the Model Ops create panel drove it —
 * this provider only hoists that state above the tab so it is durable.
 */
interface SentinelStudioContextValue {
  /** The staged design engine: context → plan → run → evaluate (SSE). */
  api: ReturnType<typeof useSentinelDesignApi>
  /** The editable draft plan (null before the agent proposes one). */
  plan: SentinelPlan | null
  dispatchPlan: Dispatch<PlanAction>
  source: SentinelSource
  setSource: Dispatch<SetStateAction<SentinelSource>>
  /** Slug of the FIM asset template the build was seeded from, if any. */
  selectedTemplateSlug: string | null
  setSelectedTemplateSlug: Dispatch<SetStateAction<string | null>>
  sentinelName: string
  setSentinelName: Dispatch<SetStateAction<string>>
  notes: string
  setNotes: Dispatch<SetStateAction<string>>
  /** Run context → plan → run → evaluate unattended after the context builds. */
  fullyAgentic: boolean
  setFullyAgentic: Dispatch<SetStateAction<boolean>>
  /** The stage the builder is expanded on (null = collapsed to the list). */
  openStage: SentinelStageId | null
  setOpenStage: Dispatch<SetStateAction<SentinelStageId | null>>
  /** True when the builder view is showing (vs. the saved-models list). */
  builderOpen: boolean
  setBuilderOpen: Dispatch<SetStateAction<boolean>>
  /** The saved model whose detail (Overview/Monitor) is open, if any. */
  selectedEntryId: string | null
  setSelectedEntryId: Dispatch<SetStateAction<string | null>>
  /** Context id to resume the builder on (set when the list reopens an
   * unfinished design or a finished run's "continue from" stage). */
  resumeContextId: string | null
  setResumeContextId: Dispatch<SetStateAction<string | null>>
  /**
   * Set when a case bubble on the chart is clicked: the tab pops open on that
   * sentinel's detail with the case in view. The sidebar-opening side effect
   * watches this; the detail view reads it to land on the Cases tab and expand
   * the clicked case. A monotonically-increasing `nonce` lets a repeat click on
   * the same case re-fire the effect. */
  focusCase: { entryId: string; caseId: string; nonce: number } | null
  focusCaseBubble: (entryId: string, caseId: string) => void
  clearFocusCase: () => void
  /** Entry ids ticked for side-by-side comparison. */
  compareIds: string[]
  toggleCompare: (entryId: string) => void
  /** Flips true when the tab is first opened; gates chart-facing side effects
   * so a chart whose Sentinels tab was never opened stays inert. */
  engaged: boolean
  markEngaged: () => void
  /** True only for gecko-internal users (the v1 gate; the tab is built
   * customer-shaped but stays internal until exposure is flipped on). */
  studioEnabled: boolean
  /** Entry pool for resolving tags → chartable pens and tag-metadata labels. */
  sensorEntryPool: BaseSensorEntry[]
  /** Points the draft plan's cleaning/gates would drop, from the live preview —
   * drawn on the chart so editing the plan visibly removes data. */
  previewRemovedPoints: ExperimentRemovedPoints[]
}

const SentinelStudioContext = createContext<SentinelStudioContextValue | null>(
  null
)

export interface SentinelStudioProviderProps {
  children: ReactNode
  siteID: string
  /** Pen-resolution pool from ScatterplotProvider (same one addSentinel uses). */
  sensorEntryPool: BaseSensorEntry[]
}

export const SentinelStudioProvider = ({
  children,
  siteID,
  sensorEntryPool,
}: SentinelStudioProviderProps) => {
  const api = useSentinelDesignApi(siteID || null)
  const [plan, dispatchPlan] = useReducer(nullablePlanReducer, null)
  const [source, setSource] = useState<SentinelSource>('template')
  const [selectedTemplateSlug, setSelectedTemplateSlug] = useState<
    string | null
  >(null)
  const [sentinelName, setSentinelName] = useState('')
  const [notes, setNotes] = useState('')
  const [fullyAgentic, setFullyAgentic] = useState(false)
  const [openStage, setOpenStage] = useState<SentinelStageId | null>(null)
  const [builderOpen, setBuilderOpen] = useState(false)
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null)
  const [resumeContextId, setResumeContextId] = useState<string | null>(null)
  const [focusCase, setFocusCase] = useState<{
    entryId: string
    caseId: string
    nonce: number
  } | null>(null)
  const focusNonce = useRef(0)
  const [compareIds, setCompareIds] = useState<string[]>([])

  // Same gate as the Experiment Lab and useSentinelScores: gecko-internal only
  // for v1. The tab is built customer-shaped so exposure is a later gate flip,
  // not a rewrite.
  const { hasPermissions } = useGeckoPermissions()
  const studioEnabled = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  const [engaged, setEngaged] = useState(false)
  const markEngaged = useCallback(() => setEngaged(true), [])

  // Clicking a case bubble opens that sentinel's detail on its Cases; the nonce
  // lets clicking the same bubble again re-trigger the open.
  const focusCaseBubble = useCallback((entryId: string, caseId: string) => {
    focusNonce.current += 1
    setSelectedEntryId(entryId)
    setBuilderOpen(false)
    setFocusCase({ entryId, caseId, nonce: focusNonce.current })
  }, [])
  const clearFocusCase = useCallback(() => setFocusCase(null), [])

  const toggleCompare = useCallback((entryId: string) => {
    setCompareIds((prev) =>
      prev.includes(entryId)
        ? prev.filter((id) => id !== entryId)
        : [...prev, entryId]
    )
  }, [])

  // Live plan preview: a plan IS an ExperimentConfig, so we reuse the
  // experiment data-preview endpoint to see which points the cleaning plan /
  // gates would DROP, and redraw them on the chart as the plan is edited. This
  // is what makes "editing the plan removes stuff on the graph" live.
  const previewApi = useExperimentApi(siteID || null)
  const { fetchPreview } = previewApi
  const cleaningKey = JSON.stringify(plan?.cleaning_plan ?? null)
  const driversKey = (plan?.drivers ?? []).join('|')
  useEffect(() => {
    if (
      !studioEnabled ||
      !builderOpen ||
      !plan?.target ||
      !plan.drivers.length
    ) {
      return
    }
    const timer = window.setTimeout(() => {
      void fetchPreview(plan)
    }, 350)
    return () => window.clearTimeout(timer)
    // Keyed on the plan fields the preview depends on, not object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    studioEnabled,
    builderOpen,
    plan?.target,
    driversKey,
    plan?.start_date,
    plan?.end_date,
    plan?.interval_minutes,
    plan?.gate_scope,
    cleaningKey,
    fetchPreview,
  ])
  const previewRemovedPoints = useMemo<ExperimentRemovedPoints[]>(
    () => (builderOpen ? buildRemovedPointOverlays(previewApi.preview) : []),
    [builderOpen, previewApi.preview]
  )

  const value = useMemo(
    () => ({
      api,
      plan,
      dispatchPlan,
      source,
      setSource,
      selectedTemplateSlug,
      setSelectedTemplateSlug,
      sentinelName,
      setSentinelName,
      notes,
      setNotes,
      fullyAgentic,
      setFullyAgentic,
      openStage,
      setOpenStage,
      builderOpen,
      setBuilderOpen,
      selectedEntryId,
      setSelectedEntryId,
      resumeContextId,
      setResumeContextId,
      focusCase,
      focusCaseBubble,
      clearFocusCase,
      compareIds,
      toggleCompare,
      engaged,
      markEngaged,
      studioEnabled,
      sensorEntryPool,
      previewRemovedPoints,
    }),
    [
      api,
      plan,
      source,
      selectedTemplateSlug,
      sentinelName,
      notes,
      fullyAgentic,
      openStage,
      builderOpen,
      selectedEntryId,
      resumeContextId,
      focusCase,
      focusCaseBubble,
      clearFocusCase,
      compareIds,
      toggleCompare,
      engaged,
      markEngaged,
      studioEnabled,
      sensorEntryPool,
      previewRemovedPoints,
    ]
  )

  return (
    <SentinelStudioContext.Provider value={value}>
      {children}
    </SentinelStudioContext.Provider>
  )
}

export const useSentinelStudio = (): SentinelStudioContextValue => {
  const context = useContext(SentinelStudioContext)
  if (!context) {
    throw new Error(
      'useSentinelStudio must be used within a SentinelStudioProvider'
    )
  }
  return context
}

const NOOP_FOCUS = (): void => {}

/**
 * The current case-bubble focus signal (or null), null-safe. The chart shell
 * watches it to pop the sidebar open on the Sentinels tab when a bubble is
 * clicked.
 */
export const useSentinelStudioFocusSignal = (): {
  entryId: string
  caseId: string
  nonce: number
} | null => {
  const context = useContext(SentinelStudioContext)
  return context?.focusCase ?? null
}

/**
 * The case-bubble click handler, null-safe for chart surfaces rendered without
 * a SentinelStudioProvider (they get a no-op, so a bubble click simply does
 * nothing rather than throwing).
 */
export const useSentinelStudioFocusCase = (): ((
  entryId: string,
  caseId: string
) => void) => {
  const context = useContext(SentinelStudioContext)
  return context?.focusCaseBubble ?? NOOP_FOCUS
}

const ONE_DAY_MS = 24 * 60 * 60 * 1000
const dayMs = (date: string): number | null => {
  const ms = Date.parse(`${date}T00:00:00Z`)
  return Number.isFinite(ms) ? ms : null
}

interface PlanSplitRange {
  startMs: number
  endMs: number
  role: string
}

/**
 * The draft plan's train/val/test windows as epoch-ms spans, so the chart can
 * shade them WHILE designing — the honest split (a design-time concept) the
 * served band cannot show. Handles the two common split modes: explicit custom
 * date ranges, and the tail-fraction default. Chunked (month/year/N-day) splits
 * are computed on the backend, so we omit their preview rather than guess.
 */
function planSplitRangesMs(plan: SentinelPlan): PlanSplitRange[] {
  const split = plan.split
  if (!split) return []
  if (split.unit === 'custom' && split.custom_ranges?.length) {
    return split.custom_ranges
      .map((range) => {
        const startMs = dayMs(range.start)
        const endMs = dayMs(range.end)
        return startMs !== null && endMs !== null && endMs > startMs
          ? { startMs, endMs, role: range.role || 'train' }
          : null
      })
      .filter((range): range is PlanSplitRange => range !== null)
  }
  if (split.unit === 'tail_frac') {
    const start = dayMs(plan.start_date)
    const endDay = dayMs(plan.end_date)
    if (start === null || endDay === null) return []
    const end = endDay + ONE_DAY_MS // end_date is an inclusive day
    const frac = Math.min(Math.max(split.train_frac ?? 0.9, 0.01), 0.99)
    const cut = start + (end - start) * frac
    return [
      { startMs: start, endMs: cut, role: 'train' },
      { startMs: cut, endMs: end, role: 'test' },
    ]
  }
  return []
}

const EMPTY_OVERLAYS = {
  splitRanges: [] as PlanSplitRange[],
  testBoundaryMs: null as number | null,
  target: null as string | null,
}

/**
 * Chart-facing view of the sentinel-studio build overlays: the draft plan's
 * split regions to shade on the builder chart, only while the builder is open
 * and a plan with a target exists. Null-safe: chart surfaces without a
 * SentinelStudioProvider draw nothing extra.
 */
export const useSentinelStudioChartOverlays = (): {
  splitRanges: PlanSplitRange[]
  /** Epoch-ms start of the planned test window, for a marker line. */
  testBoundaryMs: number | null
  /** The target pen the split preview belongs to. */
  target: string | null
  /** Points the draft plan's cleaning/gates would drop, live. */
  removedPoints: ExperimentRemovedPoints[]
} => {
  const context = useContext(SentinelStudioContext)
  const plan = context?.plan ?? null
  const active = !!context?.builderOpen
  const removedPoints = context?.previewRemovedPoints ?? EMPTY_REMOVED
  return useMemo(() => {
    if (!active || !plan?.target) {
      return { ...EMPTY_OVERLAYS, removedPoints }
    }
    const splitRanges = planSplitRangesMs(plan)
    const test = splitRanges.find((range) => range.role === 'test')
    return {
      splitRanges,
      testBoundaryMs: test?.startMs ?? null,
      target: plan.target,
      removedPoints,
    }
  }, [active, plan, removedPoints])
}

const EMPTY_REMOVED: ExperimentRemovedPoints[] = []
